import axios from 'axios'
import { debounce } from 'lodash-es'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHelpers } from '@/composables/useHelpers'
import {
  MARKED_TRANSACTION_KOM_FIELDS,
  MARKED_TRANSACTION_LAND_FIELDS,
  SELECTION_TABLE_UI_ONLY_FIELDS,
  TRANSACTION_EHR_INPUTFIELDS,
  TRANSACTION_EHR_BUILDING_PART_INPUTFIELDS,
} from '@/constants'
import {
  buildComparisonTableConfig,
  defaultUnitPriceBase,
  resolveComparisonContext,
} from '@/config/comparisonTableFields'
import {
  applyDualAreaColumns,
  collapseDualAreaForExport,
} from '@/services/areaDisplay'
import { orderBuildingsHooneFirst } from '@/services/buildingSelection'
import { soldBuildingPart } from '@/services/soldBuildingPart'
import {
  isMasterLandField,
  isMasterPivotField,
  MASTER_LAND_FIELDS,
  MASTER_PIVOT_FIELDS,
  masterLandSavePayload,
  masterPivotSavePayload,
  PIVOT_FIELD_KEYS,
} from '@/services/masterPivotFields'
import {
  comparisonKeyFor,
  comparisonLabelFor,
  labelForUnchangedCommit,
} from '@/services/comparisonLabel'
import { num } from '@/services/utilities'
import { useDetailStore } from './useDetailStore'
import { useEvaluationStore } from './useEvaluationStore'
import { useInfraStore } from './useInfraStore'

export const useTransactionStore = defineStore('transaction', () => {
  const {
    composeData,
    calculatePrice,
    calculateUnitPrice,
    formatLandTechnical,
    sumArray,
    sumArrayAbs,
  } = useHelpers()
  const { t } = useI18n()

  const detailStore = useDetailStore()
  const evaluationStore = useEvaluationStore()

  const M2_LABEL_FIELDS = ['ajal_hind', 'kohandatud_hind', 'turuvaartus']

  const resolveFieldLabel = field => {
    const baseLabel = t(`messages.markedtransactions.fields.${field}`)
    if (
      selectedPriceBase.value === 'm2hind' &&
      M2_LABEL_FIELDS.includes(field)
    ) {
      return baseLabel.replace(/, eur$/, ', eur/m\u00B2')
    }
    return baseLabel
  }

  const isTransactionsMapDialogOpen = ref(false)
  const transactionsAbortController = ref(new AbortController())

  const transactionDetails = ref({})
  const transactions = ref([])
  const selectedTransaction = ref(null)
  const selectedTransactionIds = ref([])
  const selectedTransactions = ref([])
  const transactionMapRequestParams = ref({
    filters: {},
    label: null,
    bbox: null,
    zoom: null,
  })
  const relatedTransactions = ref([])
  const markedTransactions = ref([])
  const buildings = ref({})
  const selectedUnitPriceBase = ref('hooneosa_pind')
  const selectedPriceBase = ref('tehingusumma')
  const masterData = ref(null)
  const markedTableConf = ref({})

  // RED-296: criterion groups the user has hidden (group-head keys). A hidden
  // group disappears from the table, both exports, and the adjustment totals;
  // the underlying values stay in the changes pivot and return on unhide.
  // Persisted per evaluation in comparison_table_state.hiddenGroups.
  const hiddenComparisonGroups = ref([])

  // Group membership: the head plus every following field up to the next
  // group-head key. The only rule that holds for every registry shape (plain
  // triples, the 4-row ehitise_esmane_kasutus group, rating-based groups).
  const comparisonGroupMembers = head => {
    const conf = markedTableConf.value
    const fields = conf?.fields || []
    const groups = conf?.groups || []
    const start = fields.indexOf(head)
    if (start === -1) return []
    const members = [head]
    for (let i = start + 1; i < fields.length; i++) {
      if (groups.includes(fields[i])) break
      members.push(fields[i])
    }
    return members
  }

  const hiddenComparisonKeys = computed(() => {
    const keys = new Set()
    hiddenComparisonGroups.value.forEach(head => {
      comparisonGroupMembers(head).forEach(key => keys.add(key))
    })
    return keys
  })

  // A group is hideable when it is an adjustment criterion (has a _kohandus
  // member) before the summary tail. Never hideable: the comparable's identity
  // row (aadress), the ML residual reconciliation row (maaramata_kohandus),
  // and custom criteria (they have a delete button instead).
  const isHideableComparisonGroup = head => {
    const conf = markedTableConf.value
    if (!conf?.groups?.includes(head)) return false
    if (head.startsWith('criteria-')) return false
    if (['aadress', 'maaramata_kohandus'].includes(head)) return false
    const fields = conf.fields || []
    const headIdx = fields.indexOf(head)
    const sumIdx = fields.indexOf('sum_kohandus')
    if (headIdx === -1 || (sumIdx !== -1 && headIdx >= sumIdx)) return false
    return comparisonGroupMembers(head).some(key => key.endsWith('_kohandus'))
  }

  // RED-297: manual per-comparable overrides (the changes pivot). Tracks which
  // fields were hand-edited and what the pre-edit value was, so the UI can
  // mark edited cells and offer restore-to-original. The five ML pivot keys
  // are excluded — their flow also feeds the CSV dataset + adjustment
  // recompute, so a plain pivot-unset would leave those out of sync.
  const overriddenFields = ref({})
  const originalFieldValues = ref({})

  const isRevertibleChangeField = field => !PIVOT_FIELD_KEYS.includes(field)

  const markFieldOverridden = (txId, field, originalValue) => {
    if (!isRevertibleChangeField(field)) return
    const id = String(txId)
    if (!originalFieldValues.value[id]) {
      originalFieldValues.value[id] = {}
    }
    if (!(field in originalFieldValues.value[id])) {
      originalFieldValues.value[id][field] = originalValue
    }
    overriddenFields.value = {
      ...overriddenFields.value,
      [id]: { ...(overriddenFields.value[id] || {}), [field]: true },
    }
  }

  const clearFieldOverride = (txId, field) => {
    const id = String(txId)
    if (overriddenFields.value[id]) {
      const { [field]: _removed, ...rest } = overriddenFields.value[id]
      overriddenFields.value = { ...overriddenFields.value, [id]: rest }
    }
    if (originalFieldValues.value[id]) {
      delete originalFieldValues.value[id][field]
    }
  }

  const isFieldOverridden = (txId, field) =>
    !!overriddenFields.value[String(txId)]?.[field]

  const getOriginalFieldValue = (txId, field) =>
    originalFieldValues.value[String(txId)]?.[field]

  // Restore the pre-edit value: the backend unsets the changes-pivot key when
  // value is null, and the row gets the captured original back — derived rows
  // (m2hind, ajal_hind, kohandatud_hind, …) recompute reactively.
  const revertTransactionField = async (transactionId, fieldKey) => {
    const idAsNumber = Number(transactionId)
    const targetRow = selectionTableRows.value.find(t => t.id === idAsNumber)
    const evaluationId = evaluationStore.id
    if (!targetRow || !evaluationId) return

    const original = getOriginalFieldValue(idAsNumber, fieldKey)
    try {
      await axios.put(
        `/evaluation/${evaluationId}/transaction/${idAsNumber}/changes`,
        { field: fieldKey, value: null },
      )
      targetRow[fieldKey] = original ?? null
      clearFieldOverride(idAsNumber, fieldKey)
    } catch (error) {
      console.error('Failed to revert transaction field:', error)
    }
  }

  // A manual `*_kohandus` edit rewrites the `*_vordlus` word above it from
  // the sign (Parem / Kehvem / Samaväärne), the same words the AI adjustment
  // carries — valuers changed the number and forgot the word (helpdesk,
  // 2026-09). Always overwrites, even a hand-typed word; the word cell stays
  // editable on top. Goes through updateComparisonTransactionField so the
  // sibling gets the same PUT, rollback and edited-marker bookkeeping.
  const syncComparisonLabel = async (
    transactionId,
    fieldKey,
    adjustmentValue,
    targetObject,
  ) => {
    const vordlusKey = comparisonKeyFor(fieldKey)
    if (!vordlusKey) return
    // Excludes ajal_kohandus / sum_kohandus / kohanduse_abs (not `kohandus`)
    // and maaramata_kohandus (no editable sibling). User-defined criteria
    // register both halves, so they qualify.
    if (!markedTableConf.value?.kohandus?.includes(fieldKey)) return
    if (!markedTableConf.value?.editables?.includes(vordlusKey)) return

    const label = comparisonLabelFor(adjustmentValue)
    if (label === null || targetObject[vordlusKey] === label) return

    await updateComparisonTransactionField({
      transactionId,
      fieldKey: vordlusKey,
      value: label,
    })
  }

  // RED-313: hand-edited "Hinnatav vara" cells that have no persisted home
  // (area, rooms, floors, networks, muu_info, …). Session-only: applied last by
  // storeMasterData() so they survive the ComparisonTable remount, dropped
  // when the subject's building part changes or the table is torn down.
  // Persisting them later = saving this map under comparison_table_state.
  const masterOverrides = ref({})

  const clearMasterOverrides = () => {
    masterOverrides.value = {}
  }

  const isEmptyValue = v =>
    v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

  const objectId = ref(null)
  // const selectionTableExport = ref({})
  // const comparisonTableExport = ref({})
  const selectedBuilding = ref({})

  // Accumulates pivot field overrides for debounced recalculation
  // Structure: { transactionId: { fieldName: value, ... }, ... }
  const pendingPivotOverrides = ref({})

  const selectionTableRows = ref([])
  const selectionTableRowsParsed = ref([])
  const selectionTableFields = ref([])
  const selectionTableSwitchState = ref({})

  const shouldInitTransactionFilters = ref(false)
  const searchMetadata = ref({
    filterOptions: {},
    ranges: {},
    count: null,
    pagination: {
      current_page: 1,
      per_page: 1000,
      total: 0,
      last_page: 1,
    },
  })
  const filterMetadataLoading = ref(false)

  const comparisonComponentPage = ref(0)

  // RED-235: build one Ostu-müügitehingute (selection) table row. For the
  // "Hooneosa pind" row, an üksikelamu column shows the dual "suletud netopind /
  // eluruumi pind" area — kept as two separate, individually editable values
  // (eluruumi under the column id, SNP under `<id>__snp`) so the user can
  // correct either one (Toomas RED-235 Q6). The raw numeric hooneosa_pind stays
  // the eluruumi value so the m²-price calculation keeps working; the two
  // values are only joined for export (collapseDualAreaForExport).
  const buildSelectionRow = field => {
    const row = {
      key: field,
      label: t(`messages.markedtransactions.fields.${field}`),
      error: false,
    }
    processedSelectionTableRows.value.forEach(transaction => {
      row[transaction.id + ''] = transaction[field]
    })

    if (field === 'hooneosa_pind') {
      const anyHouse = applyDualAreaColumns(
        row,
        processedSelectionTableRows.value.map(transaction => ({
          id: transaction.id,
          snp: transaction.ehitise_suletud_netopind,
          isHouse: !!transaction.is_house_for_area,
        })),
      )
      if (anyHouse) {
        row.label = t('messages.markedtransactions.fields.hooneosa_pind_house')
      }
    }

    return row
  }

  // RED-267: the Ostu-müügitehingute row set is context-dependent — land
  // evaluations swap the building rows for the land element set.
  const selectionTableFieldKeys = computed(() =>
    evaluationStore.isLandParcel
      ? MARKED_TRANSACTION_LAND_FIELDS
      : MARKED_TRANSACTION_KOM_FIELDS,
  )

  // The one choke point for both docx exports (Expert + Kiirhindamine): rows
  // that are on-screen only (SELECTION_TABLE_UI_ONLY_FIELDS) never leave here.
  const selectionTableExport = computed(() => {
    return {
      cols: selectionTableFieldsComputed.value,
      rows: collapseDualAreaForExport(
        selectionTableFieldKeys.value
          .filter(field => !SELECTION_TABLE_UI_ONLY_FIELDS.includes(field))
          .map(buildSelectionRow),
      ),
    }
  })

  const comparisonTableExport = computed(() => {
    return {
      cols: comparisonTableFields.value,
      rows: collapseDualAreaForExport(
        comparisonTableRows.value.filter(row => !row.isPrediction && !row.hidden),
      ),
    }
  })

  const processedSelectionTableRows = computed(() => {
    return selectionTableRows.value.map(t => ({
      ...t,
      m2hind: calculateUnitPrice(t, selectedUnitPriceBase.value),
      vara_pind: t[selectedUnitPriceBase.value],
    }))
  })

  const selectionTableFieldsComputed = computed(() => {
    if (selectionTableRows.value.length === 0) return []
    const thWidth = 100 / (selectionTableRows.value.length + 1)
    const transactionFields = selectionTableRows.value.map(transaction => ({
      key: transaction.id + '',
      id: transaction.id,
      label: transaction.id + '',
      thStyle: { width: thWidth + '%' },
      tdClass: 'td-cell',
    }))

    return [
      {
        key: 'label',
        label: '',
        thStyle: { width: thWidth + '%' },
        tdClass: 'td-cell',
      },
      ...transactionFields,
    ]
  })

  const selectionTableRowsParsedComputed = computed(() => {
    return selectionTableFieldKeys.value.map(buildSelectionRow)
  })

  const comparableTransactions = computed(() => {
    return selectionTableRows.value.filter(t => t.is_comparable)
  })

  const processedComparableTransactions = computed(() => {
    // RED-296: hidden groups drop out of the totals too, so sum_kohandus /
    // kohandatud_hind always match the rows the table and the exports show.
    const hiddenKeys = hiddenComparisonKeys.value
    const sumKohandusFields = (markedTableConf.value?.kohandus || []).filter(
      field => !hiddenKeys.has(field),
    )
    const kohanduseAbsFields = (markedTableConf.value?.kohandus_abs || []).filter(
      field => !hiddenKeys.has(field),
    )

    return comparableTransactions.value.map(t => {
      const newT = { ...t }
      newT.m2hind = calculateUnitPrice(newT, selectedUnitPriceBase.value)
      newT.vara_pind = newT[selectedUnitPriceBase.value]
      newT.ajal_hind = calculatePrice(
        newT,
        selectedPriceBase.value,
        newT.ajal_kohandus,
      )
      newT.sum_kohandus = sumArray(sumKohandusFields.map(f => newT[f]))
      newT.kohandatud_hind = calculatePrice(
        newT,
        'ajal_hind',
        newT.sum_kohandus,
      )
      newT.kohanduse_abs = sumArrayAbs(kohanduseAbsFields.map(f => newT[f]))
      newT.kaalutud_keskmine = Math.round(
        newT.kohandatud_hind * (num(newT.kaalud) / 100),
      )
      newT.kaalud = newT.kaalud || 0
      return newT
    })
  })

  const processedMasterData = computed(() => {
    if (!masterData.value) return null

    const master = { ...masterData.value }

    master.kaalud = sumArray(
      processedComparableTransactions.value.map(t => t.kaalud),
    )
    master.kaalutud_keskmine = null

    master.weight_avg = sumArray(
      processedComparableTransactions.value.map(t => t.kaalutud_keskmine),
    )

    master.vara_pind = master[selectedUnitPriceBase.value]

    master.turuvaartus = master.weight_avg

    if (selectedPriceBase.value === 'm2hind') {
      master.turuvaartus_umardatud =
        Math.round((master.weight_avg * master.vara_pind) / 1000) * 1000
    }

    return master
  })

  const comparisonTableData = computed(() => {
    if (!processedMasterData.value) return []
    return [processedMasterData.value, ...processedComparableTransactions.value]
  })

  const comparisonTableFields = computed(() => {
    if (comparisonTableData.value.length === 0) return []
    const thWidth = 100 / (comparisonTableData.value.length + 1)
    const transactionFields = processedComparableTransactions.value.map(
      (transaction, i) => ({
        key: transaction.id + '',
        id: transaction.id,
        label: `${t('messages.markedtransactions.comparable-property')} ${i + 1}`,
        thStyle: { width: thWidth + '%' },
        tdClass: 'td-cell',
      }),
    )

    return [
      {
        key: 'label',
        id: 'label',
        label: '',
        thStyle: { width: thWidth + '%' },
        tdClass: 'td-cell',
      },
      {
        key: 'master',
        id: 'master',
        label: t('messages.markedtransactions.evaluated-property'),
        thStyle: { width: thWidth + '%' },
        tdClass: 'td-cell',
      },
      ...transactionFields,
      {
        key: 'btn',
        id: 'btn',
        label: '',
        thStyle: { width: '50px' },
        tdClass: 'td-cell',
      },
    ]
  })

  // RED-235: subject (Hinnatav vara) is an üksikelamu → the area row shows
  // "suletud netopind / eluruumi pind" side by side instead of a single value.
  // The flag is computed on the backend (EhrBuilding::isHousePurposeText) and
  // carried on the subject's EHR general block; '' or a missing flag means any
  // other otstarve → unchanged single-area behaviour.
  const isSubjectHouse = computed(
    () => detailStore.ehrGeneral?.['is-house-for-area'] === '1',
  )

  const comparisonTableRows = computed(() => {
    const defaultFields = markedTableConf.value?.fields || []
    const userDefinedFields = markedTableConf.value?.user || {}
    const groupStartFields = markedTableConf.value?.groups || []

    const hasPredictions =
      evaluationStore.prediction?.similar_properties &&
      evaluationStore.prediction.similar_properties.length > 0

    return defaultFields.flatMap(field => {
      // RED-296: a hidden group collapses to its head row, marked hidden: true
      // so the UI can render it dimmed and restorable in place; its sub-rows
      // and generated AI `_raw` child drop out. Exports filter on row.hidden.
      const isHidden = hiddenComparisonKeys.value.has(field)
      if (isHidden && !groupStartFields.includes(field)) {
        return []
      }

      // Residual "Määramata" row renders only when a comparable actually carries
      // a leftover (legacy ML "Unknown") adjustment; clean rows hide it entirely
      // while it still contributes 0 to sum_kohandus (so totals never shift).
      if (field === 'maaramata_kohandus') {
        const hasResidual = comparisonTableData.value.some(
          tx =>
            typeof tx.maaramata_kohandus === 'number' &&
            Math.abs(tx.maaramata_kohandus) > 0,
        )
        if (!hasResidual) {
          return []
        }
      }

      const originalRow = {
        key: field,
        label: Object.keys(userDefinedFields).includes(field)
          ? userDefinedFields[field]
          : resolveFieldLabel(field),
        error: false, // TODO: Handle errors reactively
        group: groupStartFields.includes(field),
      }

      comparisonTableData.value.forEach(transaction => {
        originalRow[transaction.id + ''] = transaction[field]
      })

      // RED-262: which comparable columns hold an ML-imputed value for this
      // field (drives the AI badge; cleared when the user edits the cell).
      const imputedBy = {}
      comparisonTableData.value.forEach(transaction => {
        if (transaction.imputed_fields?.[field]) {
          imputedBy[transaction.id + ''] = true
        }
      })
      if (Object.keys(imputedBy).length > 0) {
        originalRow.imputedBy = imputedBy
      }

      // RED-235: show the dual "suletud netopind / eluruumi pind" area per
      // column, but only for the transactions whose own building is an
      // üksikelamu. The subject (master) column uses the subject-level
      // isSubjectHouse flag; comparable columns use their per-transaction
      // is_house_for_area flag — so a non-üksikelamu comparable keeps its single
      // value and never shows the building-SNP / unit-area pairing. The SNP and
      // eluruumi values are kept separate (eluruumi under the column id, SNP
      // under `<id>__snp`) so each is individually editable (Q6); they are only
      // joined for export (collapseDualAreaForExport). The row is re-labelled
      // when at least one column is an üksikelamu.
      if (field === 'vara_pind') {
        const anyHouse = applyDualAreaColumns(
          originalRow,
          comparisonTableData.value.map(transaction => ({
            id: transaction.id,
            snp: transaction.ehitise_suletud_netopind,
            isHouse:
              transaction.id === 'master'
                ? isSubjectHouse.value
                : !!transaction.is_house_for_area,
          })),
        )
        if (anyHouse && !Object.keys(userDefinedFields).includes(field)) {
          originalRow.label = t(
            'messages.markedtransactions.fields.vara_pind_house',
          )
        }
      }

      // Houses: the valuers' report prose calls this element "Kinnistu
      // suurus"; land keeps "Maatüki pindala" (a plot, not a registered
      // immovable). A user-renamed label still wins.
      if (
        field === 'maatuki_pindala' &&
        markedTableConf.value?.context === 'built' &&
        !Object.keys(userDefinedFields).includes(field)
      ) {
        originalRow.label = t(
          'messages.markedtransactions.fields.maatuki_pindala_house',
        )
      }

      if (isHidden) {
        originalRow.hidden = true
        return [originalRow]
      }

      const rows = [originalRow]

      if (
        field === 'turuvaartus' &&
        selectedPriceBase.value === 'm2hind'
      ) {
        const umardatudRow = {
          key: 'turuvaartus_umardatud',
          label: t('messages.markedtransactions.fields.turuvaartus_umardatud'),
          error: false,
          group: true,
        }
        comparisonTableData.value.forEach(transaction => {
          umardatudRow[transaction.id + ''] =
            transaction.turuvaartus_umardatud ?? ''
        })
        rows.push(umardatudRow)
      }

      if (
        hasPredictions &&
        field.includes('_kohandus') &&
        field !== 'sum_kohandus' &&
        field !== 'koogimoobel_kohandus' &&
        field !== 'muu_info_kohandus' &&
        field !== 'lisaruumid_kohandus' &&
        field !== 'maaramata_kohandus' &&
        field !== 'juurdepaas_kohandus' &&
        field !== 'ehitusoigus_kohandus' &&
        field !== 'haljastus_kohandus'
      ) {
        const rawFieldKey = `${field}_raw`
        const rawRow = {
          key: rawFieldKey,
          label: 'AI',
          isPrediction: true,
        }

        comparisonTableData.value.forEach(transaction => {
          const id = transaction.id + ''
          if (id === 'master') {
            rawRow[id] = '' // No prediction for master
            return
          }

          const adjustments =
            evaluationStore.getSimilarTransactionById(transaction.id)
              ?.adjustments || {}
          const rawValue = adjustments[rawFieldKey]

          // Set the value, defaulting to an empty string if it's null or undefined
          rawRow[id] =
            rawValue !== null && rawValue !== undefined ? rawValue : ''
        })
        rows.push(rawRow)
      }

      return rows
    })
  })

  const suppressMarkedSync = ref(false)

  watch(
    markedTransactions,
    ids => {
      if (suppressMarkedSync.value) {
        return
      }
      if (evaluationStore.id) {
        evaluationStore.updateMarkedTransactions(ids)
      }
    },
    { deep: true },
  )

  const ensureTransactionIsInSelectionTable = async transactionIdOrIds => {
    return new Promise(async (resolve, reject) => {
      const ids = Array.isArray(transactionIdOrIds)
        ? transactionIdOrIds
        : [transactionIdOrIds]
      const isMultiple = Array.isArray(transactionIdOrIds)

      const results = []
      const idsToLoad = []

      // Check which transactions are already in the table
      for (const transactionId of ids) {
        const existingRow = selectionTableRows.value.find(
          row => row.id === transactionId,
        )
        if (existingRow) {
          results.push(existingRow)
        } else {
          idsToLoad.push(transactionId)
        }
      }

      // Load missing transactions in parallel
      if (idsToLoad.length > 0) {
        const loadPromises = idsToLoad.map(async transactionId => {
          const [transaction, transactionBuildings] = await Promise.all([
            loadTransactionDetails(transactionId, evaluationStore.id),
            getOrLoadTransactionBuildings(transactionId),
          ])
          return {
            transaction,
            transactionBuildings: transactionBuildings[0],
            transactionId,
          }
        })

        const loadedData = await Promise.all(loadPromises)

        // Compose data and add to table
        const newRows = loadedData.map(
          ({ transaction, transactionBuildings }) => {
            const newRow = composeData(transaction, transactionBuildings, {
              isLand: evaluationStore.isLandParcel,
            })
            selectionTableRows.value.push(newRow)
            return newRow
          },
        )

        results.push(...newRows)
      }

      // Sort results to match original order
      const sortedResults = ids.map(id => results.find(row => row.id === id))

      resolve(isMultiple ? sortedResults : sortedResults[0])
    }).catch(err => {
      console.error('Failed to ensure transaction is in selection table:', err)
      reject(err.toString())
    })
  }

  const updateComparisonTransactionField = async ({
    transactionId,
    fieldKey,
    value,
  }) => {
    const isMaster = transactionId === 'master'
    const idAsNumber = Number(transactionId)

    let targetObject
    if (isMaster) {
      targetObject = masterData.value
    } else {
      targetObject = selectionTableRows.value.find(t => t.id === idAsNumber)
    }

    if (!targetObject) {
      console.warn(`Object with id ${transactionId} not found for update.`)
      return
    }

    // RED-313: the subject's vara_pind is derived (processedMasterData
    // re-reads master[selectedUnitPriceBase] every tick), so an edit of that
    // cell lands on the base it derives from — the same key the RED-235 dual
    // cell writes. Comparable rows keep their own vara_pind.
    const targetKey =
      isMaster && fieldKey === 'vara_pind' ? selectedUnitPriceBase.value : fieldKey

    const oldValue = targetObject[targetKey]

    let finalValue = value
    if (markedTableConf.value?.numeric?.includes(targetKey)) {
      // null = the valuer emptied the cell (see ComparisonTable's numeric
      // gate); anything else must coerce to a finite number — the table gates
      // text before it gets here, this keeps a NaN out of every derived cell.
      finalValue = isEmptyValue(value) ? null : num(value)
      if (finalValue !== null && !Number.isFinite(finalValue)) {
        return
      }
    }

    targetObject[targetKey] = finalValue

    // RED-262: a user edit turns an ML-imputed display value into a confirmed
    // one — drop the flag so the AI badge disappears.
    if (targetObject.imputed_fields?.[targetKey]) {
      delete targetObject.imputed_fields[targetKey]
    }

    if (oldValue === null && finalValue === '') {
      return
    }

    if (oldValue === finalValue) {
      // RED-313: tabbing through an untouched 0 adjustment saves nothing, but
      // an empty word above it still gets Samaväärne, as an edit to 0 would
      // (labelForUnchangedCommit). Comparable columns only, like the sync
      // after a real edit below.
      const vordlusKey = isMaster ? null : comparisonKeyFor(fieldKey)
      if (
        vordlusKey &&
        labelForUnchangedCommit(finalValue, targetObject[vordlusKey])
      ) {
        await syncComparisonLabel(transactionId, fieldKey, finalValue, targetObject)
      }
      return
    }

    // Check if this is a pivot field that needs CSV dataset update.
    // All five pivot fields recompute on the comparable side too (RED-262) —
    // same set the subject branch uses via isMasterPivotField.
    if (!isMaster && PIVOT_FIELD_KEYS.includes(fieldKey)) {
      try {
        // AWAIT the CSV dataset update (was fire-and-forget)
        await axios.post('/api/transactions/update-pivot-fields', {
          transaction_id: idAsNumber,
          field: fieldKey,
          value: finalValue,
        })
        console.log(
          `Updated CSV dataset for transaction ${idAsNumber}, field ${fieldKey}`,
        )

        // Accumulate this override (merges with any existing overrides for this transaction)
        pendingPivotOverrides.value = {
          ...pendingPivotOverrides.value,
          [idAsNumber]: {
            ...(pendingPivotOverrides.value[idAsNumber] || {}),
            [fieldKey]: finalValue,
          },
        }
        console.log(
          `Accumulated feature override for transaction ${idAsNumber}:`,
          pendingPivotOverrides.value[idAsNumber],
        )

        // Trigger debounced recalculation (will batch multiple rapid updates)
        debouncedRecalculateAdjustments()
      } catch (error) {
        console.error('Failed to update CSV dataset:', error)
      }
    }

    if (isMaster) {
      // RED-267: land element master edits persist into the same evaluation
      // JSON bucket their Detail-view card reads (two-way sync via the shared
      // store slice) — no flush, no AI recompute (not prediction inputs).
      if (isMasterLandField(fieldKey)) {
        const landField = MASTER_LAND_FIELDS[fieldKey]
        if (landField.store === 'infra') {
          const infraStore = useInfraStore()
          infraStore.infra[landField.field] = finalValue
        } else {
          detailStore[landField.detail][landField.field] = finalValue
        }
        evaluationStore.save(masterLandSavePayload(fieldKey, finalValue))
        return
      }

      if (isMasterPivotField(fieldKey)) {
        detailStore[MASTER_PIVOT_FIELDS[fieldKey].detail][fieldKey] = finalValue
        evaluationStore.save(masterPivotSavePayload(fieldKey, finalValue))

        // RED-262 (Toomas): subject pivot values are prediction inputs — the
        // backend recompute re-reads the evaluation row, so flush the debounced
        // save first, then recompute the AI adjustments exactly like
        // comparable-side pivot edits do. Without this the kohandustabel keeps
        // showing adjustments computed against the pre-edit subject.
        await evaluationStore.flushSave()
        debouncedRecalculateAdjustments()
      }

      // RED-313: every other subject cell is session-only. masterData is a
      // snapshot that storeMasterData() rebuilds from the detail stores on each
      // ComparisonTable mount (page toggle) and on building-part selection;
      // remembering the edit here is what keeps it alive across the rebuild.
      masterOverrides.value = {
        ...masterOverrides.value,
        [targetKey]: finalValue,
      }

      return
    }

    if (TRANSACTION_EHR_BUILDING_PART_INPUTFIELDS.includes(fieldKey)) {
      // RED-355: the sold part (the one training reads), not the lowest id.
      const buildingPart = soldBuildingPart(transactionDetails.value[idAsNumber])

      if (buildingPart) {
        updateTransactionBuildingPart({
          id: buildingPart.id,
          field: fieldKey,
          value: finalValue,
          transaction_id: targetObject.id,
        })
      } else {
        console.warn(
          `No building parts found for transaction ${idAsNumber}, skipping update for ${fieldKey}`,
        )
      }
      return
    }

    if (!TRANSACTION_EHR_INPUTFIELDS.includes(fieldKey)) {
      const evaluationId = evaluationStore.id
      if (evaluationId) {
        try {
          await axios.put(
            `/evaluation/${evaluationId}/transaction/${idAsNumber}/changes`,
            {
              field: fieldKey,
              value: finalValue,
            },
          )
          // RED-297: remember the pre-edit value (first edit only) so the
          // cell can show the edited marker and restore the original.
          markFieldOverridden(idAsNumber, fieldKey, oldValue)
          await syncComparisonLabel(transactionId, fieldKey, finalValue, targetObject)
        } catch (error) {
          console.error('Failed to save transaction field change:', error)
          // Revert the change on error
          targetObject[fieldKey] = oldValue
        }
      }
    } else if (TRANSACTION_EHR_INPUTFIELDS.includes(fieldKey)) {
      try {
        const transactionBuildings =
          await getOrLoadTransactionBuildings(idAsNumber)

        if (transactionBuildings && transactionBuildings.length > 0) {
          const building = transactionBuildings[0]
          building[fieldKey] = finalValue
          await updateTransactionBuilding({
            ...building,
            transaction_id: idAsNumber,
          })
        }
      } catch (error) {
        console.error('Failed to update transaction building field:', error)
      }
    }
  }

  const updateSelectionTransactionField = async ({
    transactionId,
    fieldKey,
    value,
  }) => {
    const transaction = selectionTableRows.value.find(
      t => t.id === transactionId,
    )
    if (!transaction) {
      console.warn(`Transaction with id ${transactionId} not found for update.`)
      return
    }

    const oldValue = transaction[fieldKey]
    transaction[fieldKey] = value

    if (oldValue === value) {
      return
    }

    if (TRANSACTION_EHR_BUILDING_PART_INPUTFIELDS.includes(fieldKey)) {
      // RED-355: the sold part (the one training reads), not the lowest id.
      const buildingPart = soldBuildingPart(transactionDetails.value[transactionId])

      if (buildingPart) {
        updateTransactionBuildingPart({
          id: buildingPart.id,
          field: fieldKey,
          value: value,
          transaction_id: transactionId,
        })
      } else {
        console.warn(
          `No building parts found for transaction ${transactionId}, skipping update for ${fieldKey}`,
        )
      }
      return
    }

    if (TRANSACTION_EHR_INPUTFIELDS.includes(fieldKey)) {
      try {
        const transactionBuildings =
          await getOrLoadTransactionBuildings(transactionId)
        if (transactionBuildings && transactionBuildings.length > 0) {
          const building = transactionBuildings[0]
          building[fieldKey] = value
          await updateTransactionBuilding({
            ...building,
            transaction_id: transactionId,
          })
        }
      } catch (error) {
        console.error('Failed to update transaction building field:', error)
      }
    }
  }

  const toggleTransactionIsComparable = async transactionId => {
    const transaction = selectionTableRows.value.find(
      t => t.id === transactionId,
    )
    if (transaction) {
      transaction.is_comparable = !transaction.is_comparable
      await evaluationStore.updateSelectedTransactions(transaction)
    }
  }

  // Rebuild the config for a property context, carrying user-defined custom
  // criteria over (they are context-independent and would otherwise be lost).
  const rebuildConfigPreservingCriteria = context => {
    const existingUser = markedTableConf.value?.user || {}
    const customCriteriaEntries = Object.entries(existingUser).filter(
      ([key]) => key.startsWith('criteria-') && !key.includes('_'),
    )

    markedTableConf.value = buildComparisonTableConfig({ context })

    // Re-inject custom criteria
    customCriteriaEntries.forEach(([criteriaKey, label]) => {
      const conf = markedTableConf.value
      const criteria_v = `${criteriaKey}_vordlus`
      const criteria_k = `${criteriaKey}_kohandus`
      const insertAt = conf.fields.indexOf('sum_kohandus')
      if (insertAt === -1) return

      conf.fields.splice(insertAt, 0, criteriaKey, criteria_v, criteria_k)
      conf.editables.push(criteriaKey, criteria_v, criteria_k)
      conf.master_editables.push(criteriaKey)
      conf.groups.push(criteriaKey)
      conf.numeric.push(criteria_k)
      conf.kohandus.push(criteria_k)
      conf.kohandus_abs.push(criteria_k)

      conf.user[criteriaKey] = label
      conf.user[criteria_v] = existingUser[criteria_v]
      conf.user[criteria_k] = existingUser[criteria_k]
    })
  }

  const initComparisonTableConfig = ({ hasBuildingPart } = {}) => {
    const evaluationStore = useEvaluationStore()
    const bp = hasBuildingPart ?? evaluationStore.selectedBuildingPart !== null
    const context = resolveComparisonContext({
      isLandParcel: evaluationStore.isLandParcel,
      hasBuildingPart: bp,
    })

    const fields = markedTableConf.value?.fields
    const confEmpty = !Array.isArray(fields) || fields.length === 0
    // RED-267: the reset path (Detail.vue resetForEvaluation) builds the
    // config before the isLandParcel prop / the deferred EHR building part
    // arrive, so an "already built" config can carry the wrong shape.
    // RED-296: compare the stored context directly — the previous land-ness
    // check missed apartment↔built mismatches, so a kortermaja kept its
    // maatüki rows whenever the EHR response landed while ComparisonTable
    // (and its building-part watcher) was unmounted.
    const contextMismatch = !confEmpty && markedTableConf.value.context !== context

    if (confEmpty || contextMismatch) {
      rebuildConfigPreservingCriteria(context)

      // RED-267: land unit price is per m² of the plot. Pre-C2 land
      // evaluations could never complete, so overriding a building-shaped
      // base here is safe for every land evaluation.
      if (context === 'land' && selectedUnitPriceBase.value !== 'maatuki_pindala') {
        selectedUnitPriceBase.value = 'maatuki_pindala'
      }
    }
  }

  const reconfigureForPropertyContext = hasBuildingPart => {
    rebuildConfigPreservingCriteria(
      resolveComparisonContext({
        isLandParcel: useEvaluationStore().isLandParcel,
        hasBuildingPart,
      }),
    )
  }

  const setComparisonPage = page => {
    comparisonComponentPage.value = page
  }

  const persistCustomCriteria = () => {
    if (!evaluationStore.id) return

    const conf = markedTableConf.value
    const customCriteriaKeys = Object.keys(conf.user || {}).filter(
      key => key.startsWith('criteria-') && !key.includes('_'),
    )

    if (customCriteriaKeys.length === 0) {
      evaluationStore.save({
        comparison_table_state: { customCriteria: null },
      })
      return
    }

    const customCriteria = customCriteriaKeys.map(criteriaKey => ({
      key: criteriaKey,
      label: conf.user[criteriaKey],
      vordlusLabel: conf.user[`${criteriaKey}_vordlus`],
      kohandusLabel: conf.user[`${criteriaKey}_kohandus`],
    }))

    evaluationStore.save({
      comparison_table_state: { customCriteria },
    })
  }

  // RED-296: hide/unhide criterion groups. Mirrors persistCustomCriteria —
  // the controller shallow-merges comparison_table_state key-by-key, so
  // hiddenGroups lives safely next to customCriteria.
  const persistHiddenGroups = () => {
    if (!evaluationStore.id) return
    evaluationStore.save({
      comparison_table_state: {
        hiddenGroups: hiddenComparisonGroups.value.length
          ? [...hiddenComparisonGroups.value]
          : null,
      },
    })
  }

  const hideComparisonGroup = head => {
    if (!isHideableComparisonGroup(head)) return
    if (hiddenComparisonGroups.value.includes(head)) return
    hiddenComparisonGroups.value = [...hiddenComparisonGroups.value, head]
    persistHiddenGroups()
  }

  const unhideComparisonGroup = head => {
    if (!hiddenComparisonGroups.value.includes(head)) return
    hiddenComparisonGroups.value = hiddenComparisonGroups.value.filter(
      key => key !== head,
    )
    persistHiddenGroups()
  }

  const unhideAllComparisonGroups = () => {
    if (hiddenComparisonGroups.value.length === 0) return
    hiddenComparisonGroups.value = []
    persistHiddenGroups()
  }

  // Heads absent from the current context are kept: they simply don't match
  // any field until an apartment↔built/land rebuild brings them back.
  const restoreHiddenGroups = hiddenGroups => {
    if (!Array.isArray(hiddenGroups)) return
    hiddenComparisonGroups.value = hiddenGroups.filter(
      key => typeof key === 'string',
    )
  }

  const persistPriceMode = () => {
    if (!evaluationStore.id) return
    evaluationStore.save({
      comparison_table_state: {
        selectedPriceBase: selectedPriceBase.value,
        selectedUnitPriceBase: selectedUnitPriceBase.value,
      },
    })
  }

  const restorePriceMode = (priceBase, unitPriceBase) => {
    if (priceBase && ['tehingusumma', 'm2hind'].includes(priceBase)) {
      selectedPriceBase.value = priceBase
    }
    if (unitPriceBase && ['hooneosa_pind', 'korteri_pind', 'maatuki_pindala'].includes(unitPriceBase)) {
      selectedUnitPriceBase.value = unitPriceBase
    }
  }

  const restoreCustomCriteria = customCriteria => {
    if (!customCriteria || !Array.isArray(customCriteria)) return

    initComparisonTableConfig()

    customCriteria.forEach(({ key, label, vordlusLabel, kohandusLabel }) => {
      const conf = markedTableConf.value
      const criteria = key
      const criteria_v = `${criteria}_vordlus`
      const criteria_k = `${criteria}_kohandus`

      const insertAt = conf.fields.indexOf('sum_kohandus')
      if (insertAt === -1) return

      conf.fields.splice(insertAt, 0, criteria, criteria_v, criteria_k)
      conf.editables.push(criteria, criteria_v, criteria_k)
      conf.master_editables.push(criteria)
      conf.groups.push(criteria)
      conf.numeric.push(criteria_k)
      conf.kohandus.push(criteria_k)
      conf.kohandus_abs.push(criteria_k)

      conf.user[criteria] = label
      conf.user[criteria_v] = vordlusLabel
      conf.user[criteria_k] = kohandusLabel
    })
  }

  const updateUserDefinedFieldLabel = (fieldKey, newLabel) => {
    if (markedTableConf.value?.user && fieldKey in markedTableConf.value.user) {
      markedTableConf.value.user[fieldKey] = newLabel
      persistCustomCriteria()
    }
  }

  const addComparisonCriteria = () => {
    const conf = markedTableConf.value
    const criteria = `criteria-${Object.keys(conf.user).length}`
    const criteria_v = `${criteria}_vordlus`
    const criteria_k = `${criteria}_kohandus`

    const insertAt = conf.fields.indexOf('sum_kohandus')

    conf.fields.splice(insertAt, 0, criteria, criteria_v, criteria_k)
    conf.editables.push(criteria, criteria_v, criteria_k)
    conf.master_editables.push(criteria)
    conf.groups.push(criteria)
    conf.numeric.push(criteria_k)
    conf.kohandus.push(criteria_k)
    conf.kohandus_abs.push(criteria_k)

    conf.user[criteria] = null
    conf.user[criteria_v] = t(
      `messages.markedtransactions.fields.muu_info_vordlus`,
    )
    conf.user[criteria_k] = t(
      `messages.markedtransactions.fields.muu_info_kohandus`,
    )

    selectionTableRows.value = selectionTableRows.value.map(t => {
      t[criteria] = null
      t[criteria_v] = null
      t[criteria_k] = 0
      return t
    })

    if (masterData.value) {
      masterData.value[criteria] = null
      masterData.value[criteria_v] = null
      masterData.value[criteria_k] = null
    }

    persistCustomCriteria()
  }

  const deleteComparisonCriteria = criteria => {
    const conf = markedTableConf.value
    const criteria_v = `${criteria}_vordlus`
    const criteria_k = `${criteria}_kohandus`

    const fieldsToRemove = [criteria, criteria_v, criteria_k]

    conf.fields = conf.fields.filter(f => !fieldsToRemove.includes(f))
    conf.editables = conf.editables.filter(f => !fieldsToRemove.includes(f))
    conf.master_editables = conf.master_editables.filter(f => f !== criteria)
    conf.groups = conf.groups.filter(f => f !== criteria)
    conf.numeric = conf.numeric.filter(f => f !== criteria_k)
    conf.kohandus = conf.kohandus.filter(f => f !== criteria_k)
    conf.kohandus_abs = conf.kohandus_abs.filter(f => f !== criteria_k)

    delete conf.user[criteria]
    delete conf.user[criteria_v]
    delete conf.user[criteria_k]

    selectionTableRows.value = selectionTableRows.value.map(t => {
      delete t[criteria]
      delete t[criteria_v]
      delete t[criteria_k]
      return t
    })

    if (masterData.value) {
      delete masterData.value[criteria]
      delete masterData.value[criteria_v]
      delete masterData.value[criteria_k]
    }

    persistCustomCriteria()
  }

  const restoreMarkedTransactions = async tr => {
    markedTransactions.value = tr.map(t => t.id)
    await ensureTransactionIsInSelectionTable(markedTransactions.value)

    // Apply saved changes from evaluation_transaction pivot
    tr.forEach(transaction => {
      if (transaction.changes) {
        const targetRow = selectionTableRows.value.find(
          row => row.id === transaction.id,
        )
        if (targetRow) {
          Object.entries(transaction.changes).forEach(([field, value]) => {
            // RED-297: capture the composed original before overwriting so
            // the edited marker + restore-to-original work after reload.
            markFieldOverridden(transaction.id, field, targetRow[field])
            targetRow[field] = value
          })
        }
      }
    })
  }

  const abortAndResetTransactionsAbortController = () => {
    transactionsAbortController.value.abort()
    transactionsAbortController.value = new AbortController()
  }

  // Cadastre outlines for transactions on the map (RED-255).
  // `resolution` (m/px) lets the server cull parcels too small to see at the
  // current zoom band. Returns { parcels: [{ transaction_id, tunnus, geometry }],
  // skipped_transaction_ids: [] } in EPSG:3301.
  const loadParcelBoundaries = async (ids, resolution = null) => {
    if (!ids || ids.length === 0) {
      return { parcels: [], skipped_transaction_ids: [] }
    }
    const response = await axios.post('/api/transactions/map/parcels', {
      ids,
      resolution,
    })
    return {
      parcels: response.data?.parcels || [],
      skipped_transaction_ids: response.data?.skipped_transaction_ids || [],
    }
  }

  const updateSelectionTableRows = tr => {
    tr.forEach(item => {
      const index = selectionTableRows.value.findIndex(t => t.id === item.id)
      if (index > -1) {
        selectionTableRows.value[index] = item
      }
    })
  }

  const getMarkedTransactions = computed(() => {
    return markedTransactions.value
  })

  const getTransactions = computed(() => {
    return transactions.value
  })

  const getSelectedTransactions = ids => {
    if (ids && ids.length) {
      return transactions.value.filter(transaction =>
        ids.includes(transaction.id),
      )
    }
    return transactions.value
  }

  const selectTransaction = transaction => {
    selectedTransaction.value = transaction
  }

  const updateTransaction = transaction => {
    return axios
      .put(`/api/transactions/${transaction.id}`, {
        ...transaction,
      })
      .then(() => {
        transactionDetails.value = {
          ...transactionDetails.value,
          [transaction.id]: transaction,
        }

        if (
          selectedTransaction.value &&
          selectedTransaction.value.id === transaction.id
        ) {
          selectedTransaction.value = transaction
        }

        return transaction
      })
      .catch(err => {
        console.error('Error updating transaction:', err)
        throw err
      })
  }

  // RED-278: archived listing copy for the archive dialog. Fetched on demand per
  // transaction — deliberately not part of transactionDetails / map payloads.
  const fetchAdArchive = async transactionId => {
    const { data } = await axios.get(`/api/transactions/${transactionId}/ad-archive`)
    return data
  }

  const retryAdArchive = async transactionId => {
    const { data } = await axios.post(`/api/transactions/${transactionId}/ad-archive/retry`)
    return data
  }

  // Keep the cached details + selected transaction in step with the dialog's
  // freshest archive summary (the sidebar status line reads transaction.ad_archive).
  const setAdArchiveSummary = (transactionId, summary) => {
    const cached = transactionDetails.value[transactionId]
    if (cached) {
      transactionDetails.value = {
        ...transactionDetails.value,
        [transactionId]: { ...cached, ad_archive: summary },
      }
    }
    if (selectedTransaction.value?.id === transactionId) {
      selectedTransaction.value = { ...selectedTransaction.value, ad_archive: summary }
    }
  }

  // Maatükk enrichment field → the kohandustabel row cell it feeds (RED-268).
  // The tehnovõrgud subfields compose into the single tehnovorgud cell via
  // formatLandTechnical, mirroring the composeData row builder.
  const MAATUKK_COMPARISON_CELLS = {
    juurdepaas: 'juurdepaas',
    ehitusoigus: 'ehitusoigus',
    haljastus: 'haljastus',
    muu: 'muu_info',
  }

  const MAATUKK_TEHNOVORGUD_FIELDS = [
    'elektrisysteemi_liik',
    'veevarustuse_liik',
    'kanalisatsiooni_liik',
    'soojusvarustuse_liik',
    'gaasi_olemasolu',
    'side',
  ]

  // RED-267: single-field save of the Maatükk-tab enrichment, with cache
  // write-through so a reopened sidebar (memoised transactionDetails) and the
  // live selectedTransaction both reflect the edit without a refetch.
  const updateTransactionMaatukk = async ({ transactionId, field, value }) => {
    await axios.put(`/api/transactions/${transactionId}/maatukk`, {
      field,
      value,
    })

    const applyTo = transaction => {
      if (!transaction) {
        return
      }
      const next = { ...(transaction.maatukk || {}) }
      if (value === null || value === '') {
        delete next[field]
      } else {
        next[field] = value
      }
      transaction.maatukk = Object.keys(next).length ? next : null
    }

    applyTo(transactionDetails.value[transactionId])
    if (selectedTransaction.value?.id === transactionId) {
      applyTo(selectedTransaction.value)
    }

    // RED-268: reflect the edit into an already-mounted kohandustabel row
    // (the masterData write-through precedent, comparable side). Persistence
    // needs no extra call — restored rows recompose from transaction.maatukk,
    // which the endpoint above just saved. Manual cell overrides (pivot
    // `changes`) still win on restore, as they always have.
    const row = selectionTableRows.value.find(t => t.id === transactionId)
    if (!row) {
      return
    }

    if (MAATUKK_COMPARISON_CELLS[field]) {
      row[MAATUKK_COMPARISON_CELLS[field]] =
        value === null || value === '' ? null : value
    } else if (MAATUKK_TEHNOVORGUD_FIELDS.includes(field)) {
      const source =
        transactionDetails.value[transactionId] ??
        (selectedTransaction.value?.id === transactionId
          ? selectedTransaction.value
          : null)
      if (source) {
        row.tehnovorgud = formatLandTechnical(source.maatukk)
      }
    }
  }

  const loadTransactionDetails = async (id, evaluationId = null) => {
    return new Promise(async (resolve, reject) => {
      if (transactionDetails.value[id]) {
        resolve(transactionDetails.value[id])
      }

      try {
        const response = await axios.get(`/api/transactions/${id}/details`, {
          params: {
            evaluationId,
          },
        })
        const transaction = response.data

        transactionDetails.value = {
          ...transactionDetails.value,
          [id]: transaction,
        }

        if (transaction.buildings) {
          const newBuildings = {
            ...buildings.value[id],
            ...transaction.buildings,
          }
          const ordered = orderBuildingsHooneFirst(Object.values(newBuildings))
          buildings.value[id] = ordered
          selectedBuilding.value = ordered[0]
        }

        resolve(transaction)
      } catch (err) {
        console.error(`Error fetching transaction ${id}:`, err)
        reject(err.toString())
      }
    })
  }

  // One slim page of a cluster's members for the map popover (infinite
  // scroll). Ids go in the POST body — a 99+ cluster's ids would overflow
  // the URL as a query string. No store side effects: the popover owns its
  // accumulated list.
  const loadClusterTransactions = async (ids, offset, limit) => {
    const response = await axios.post('/api/transactions/cluster-list', {
      ids,
      offset,
      limit,
    })
    return response.data.transactions
  }

  const loadTransactionsByIds = async ids => {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          `/api/transactions?ids=${ids.join(',')}`,
        )
        transactions.value = response.data.transactions
        resolve(response.data.transactions)
      } catch (err) {
        console.error('Error loading transactions by ids:', err)
        reject(err.toString())
      }
    })
  }

  const getOrLoadTransactionBuildings = id => {
    return new Promise((resolve, reject) => {
      const storedData = buildings.value
      if (storedData[id]) {
        selectedBuilding.value = storedData[id][0]

        resolve(storedData[id])
      } else {
        axios
          .get(`/api/transactions/${id}/buildings`)
          .then(response => {
            const newBuildings = {
              ...buildings.value[id],
              ...response.data.buildings,
            }
            const ordered = orderBuildingsHooneFirst(Object.values(newBuildings))
            buildings.value[id] = ordered
            selectedBuilding.value = ordered[0]
            resolve(ordered)
          })
          .catch(err => {
            selectedBuilding.value = null
            reject(err.toString())
          })
      }
    })
  }

  const loadRelatedTransactions = async transaction => {
    return new Promise((resolve, reject) => {
      axios
        .get(`/api/transactions/${transaction.id}/related`)
        .then(response => {
          resolve(response.data.transactions)
        })
        .catch(err => {
          console.log('Error: ' + err)
          reject(err.toString())
        })
    })
  }

  const loadMarkedTransactionsDetails = async () => {
    const promises = markedTransactions.value.map(id => {
      if (!transactionDetails.value[id]) {
        return loadTransactionDetails(id)
      }
      return Promise.resolve(transactionDetails.value[id])
    })
    return Promise.all(promises)
  }

  const toggleMarked = async transaction => {
    if (!transactionDetails.value[transaction.id]) {
      await loadTransactionDetails(transaction.id)
    }

    // Ensure the transaction data is present in our main list before we mark it.
    await ensureTransactionIsInSelectionTable(transaction.id)

    const index = markedTransactions.value.indexOf(transaction.id)

    if (index === -1) {
      markedTransactions.value.push(transaction.id)
    } else {
      markedTransactions.value.splice(index, 1)
    }

    // Recalculate adjustments for comparable transactions
    await nextTick() // Wait for UI to update

    const comparableIds = markedTransactions.value.filter(id => {
      const row = selectionTableRows.value.find(r => r.id === id)
      return row?.is_comparable === true
    })

    if (
      comparableIds.length > 0 &&
      evaluationStore.prediction?.similar_properties?.length > 0
    ) {
      try {
        // Carry the accumulated pivot overrides so a mark/unmark doesn't revert
        // the user's field edits to their stored dataset values (stateless recompute).
        const overridesToSend = { ...pendingPivotOverrides.value }
        const adjustmentData =
          await evaluationStore.calculateComparablesAdjustments(
            comparableIds,
            Object.keys(overridesToSend).length > 0 ? overridesToSend : null,
          )

        if (adjustmentData && adjustmentData.available) {
          await evaluationStore.updateComparablesAdjustments(adjustmentData)
        }
      } catch (error) {
        console.error('Failed to recalculate adjustments after toggle:', error)
      }
    }
  }

  const updateTransactionBuilding = async building => {
    return await axios
      .put(
        `/api/transactions/${building.transaction_id}/buildings/${building.id}`,
        {
          ...building,
        },
      )
      .then(() => {
        buildings.value[building.transaction_id][building.id] = building
        const tr = transactionDetails.value[building.transaction_id]
        const trBuilding = tr.buildings.findIndex(b => b.id === building.id)

        if (trBuilding !== -1) {
          tr.buildings[trBuilding] = building
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  const updateTransactionBuildingField = async data => {
    return await axios
      .put(`/api/transactions/${data.transaction_id}/building/${data.id}`, {
        ...data,
      })
      .then(() => {
        if (buildings.value[data.transaction_id]) {
          const building = buildings.value[data.transaction_id].find(
            b => b.id === data.id,
          )

          if (building) {
            building[data.field] = data.value
          }
        }

        const tr = transactionDetails.value[data.transaction_id]
        const trBuilding = tr.buildings.findIndex(b => b.id === data.id)

        if (trBuilding !== -1) {
          tr.buildings[trBuilding][data.field] = data.value
        }

        const targetObject = selectionTableRows.value.find(
          t => t.id === data.transaction_id,
        )

        if (targetObject) {
          targetObject[data.field] = data.value
        }
      })
      .catch(err => {
        console.log(err)
      })
  }

  const updateTransactionBuildingPart = async buildingPart => {
    return await axios
      .put(
        `/api/transactions/${buildingPart.transaction_id}/building-parts/${buildingPart.id}`,
        {
          ...buildingPart,
        },
      )
      .then(() => {
        const tr = transactionDetails.value[buildingPart.transaction_id]
        const trBuildingPart = tr.building_parts[buildingPart.id]
        if (trBuildingPart) {
          trBuildingPart[buildingPart.field] = buildingPart.value
        }

        const targetObject = selectionTableRows.value.find(
          t => t.id === buildingPart.transaction_id,
        )

        if (targetObject) {
          targetObject[buildingPart.field] = buildingPart.value
        }

        if (
          selectedTransaction.value &&
          selectedTransaction.value.id === buildingPart.transaction_id
        ) {
          const selectedBuildingPart =
            selectedTransaction.value.building_parts[buildingPart.id]

          if (selectedBuildingPart) {
            selectedBuildingPart[buildingPart.field] = buildingPart.value
          }
        }
      })
  }

  // Action to handle feature selection from the map
  const handleMapFeatureSelection = async ids => {
    selectedTransactionIds.value = ids
    selectedTransaction.value = null // Reset single selection
    transactions.value = [] // Reset list selection
    selectedTransactions.value = []
    buildings.value = {}

    if (!ids || ids.length === 0) {
      // Nothing selected or deselected
      return
    }

    try {
      if (ids.length === 1) {
        // Single feature selected
        const detail = await loadTransactionDetails(ids[0])
        selectedTransaction.value = detail
        selectedTransactions.value = [detail] // Update sidebar display state
        // Pre-load buildings for the selected transaction
        // await getOrLoadTransactionBuildings(ids[0])
      } else {
        // Cluster selected
        const clusterTransactions = await loadTransactionsByIds(ids)
        transactions.value = clusterTransactions // Store the list
        selectedTransactions.value = clusterTransactions // Update sidebar display state
      }
    } catch (error) {
      console.error('Error handling feature selection:', error)
      // Optionally handle error state, e.g., show a notification
      selectedTransactionIds.value = []
      selectedTransactions.value = []
    }
  }

  const handleListFeatureSelection = async ids => {
    selectedTransactionIds.value = ids
    selectedTransaction.value = null // Reset single selection
    // Consider clearing specific building details if necessary
    // buildings.value = {};

    if (!ids || ids.length === 0) {
      // Nothing selected or deselected
      return
    }

    try {
      if (ids.length === 1) {
        // Single feature selected
        const detail = await loadTransactionDetails(ids[0])
        selectedTransaction.value = detail
        selectedTransactions.value = [detail] // Update sidebar display state
        // Pre-load buildings for the selected transaction
        await getOrLoadTransactionBuildings(ids[0])
      } else {
        // Cluster selected
        const clusterTransactions = await loadTransactionsByIds(ids)
        transactions.value = clusterTransactions // Store the list
        selectedTransactions.value = clusterTransactions // Update sidebar display state
      }
    } catch (error) {
      console.error('Error handling feature selection:', error)
      // Optionally handle error state, e.g., show a notification
      selectedTransactionIds.value = []
      selectedTransactions.value = []
    }
  }

  // Own controller: metadata depends only on filters, so a map pan (which
  // aborts the shared transactions controller on every moveend) must not kill
  // it. A newer metadata request still supersedes an in-flight one.
  let filterMetadataAbortController = null

  const getFilterMetadata = async () => {
    filterMetadataLoading.value = true
    filterMetadataAbortController?.abort()
    const controller = new AbortController()
    filterMetadataAbortController = controller

    try {
      const response = await axios.get(
        '/api/transactions/map/filter-metadata',
        {
          params: {
            ...transactionMapRequestParams.value,
          },
          signal: controller.signal,
        },
      )
      searchMetadata.value = response.data
    } catch (error) {
      if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
        console.error('Error fetching filter metadata:', error)
      }
    } finally {
      // A superseded request must not clear the loading state of its successor
      if (controller === filterMetadataAbortController) {
        filterMetadataLoading.value = false
      }
    }
  }

  const getPredictionForSelectedTransactions = async () => {
    return await axios
      .post('/api/evaluation/prediction', selectionTableRows.value)
      .then(response => {
        return response.data
      })
  }

  const resetMarkedTransactions = (updateDb = true) => {
    if (updateDb) {
      markedTransactions.value = []
      return
    }
    suppressMarkedSync.value = true
    markedTransactions.value = []
    nextTick(() => {
      suppressMarkedSync.value = false
    })
  }

  const resetComparableState = () => {
    return new Promise((resolve, reject) => {
      // RED-267: "Tühjenda tabel" must not knock a land evaluation off its
      // per-plot-m² base — initComparisonTableConfig() below keeps the
      // land-shaped conf and therefore never re-applies the land override.
      selectedUnitPriceBase.value = defaultUnitPriceBase(
        resolveComparisonContext({
          isLandParcel: useEvaluationStore().isLandParcel,
          hasBuildingPart: useEvaluationStore().selectedBuildingPart !== null,
        }),
      )
      selectedPriceBase.value = 'tehingusumma'
      selectionTableRows.value = []
      masterData.value = null
      comparisonComponentPage.value = 0
      // Drop accumulated pivot overrides — they belong to the comparable set
      // being torn down (prevents leakage into the next evaluation/predict).
      pendingPivotOverrides.value = {}
      clearMasterOverrides()

      initComparisonTableConfig()

      resolve()
    })
  }

  /**
   * The "Tühjenda tabel" action, shared by every clear button (Detail card +
   * MarkedTransactionsTableModal). Clears the LOCAL marked list too
   * (sync-suppressed — the explicit updateMarkedTransactions([]) persists the
   * empty state). Without that the stale ids linger in the store: the sidebar
   * and the modal's counter badge still show the transactions as marked, and
   * the next marked-list mutation re-persists them through the deep watcher,
   * resurrecting the "cleared" table.
   */
  const clearComparableTable = async () => {
    resetMarkedTransactions(false)
    await resetComparableState()
    await evaluationStore.updateMarkedTransactions()

    evaluationStore.save({
      comparison_table_state: {
        comparisonComponentPage: 0,
      },
    })
  }

  /**
   * Tear down everything the kohandustabel reads when switching to another
   * evaluation. `ensureTransactionIsInSelectionTable` only ever appends, so
   * without this the previous evaluation's rows stay in `selectionTableRows`
   * and the table renders both sets of comparables side by side.
   *
   * `resetComparableState()` alone is not enough for a switch:
   *  - it deliberately keeps `markedTableConf`, because a re-predict *within*
   *    one evaluation must not drop the user's custom criteria — across
   *    evaluations those criteria are exactly what must not carry over;
   *  - it doesn't touch the marked list;
   *  - it doesn't touch `transactionDetails`, which is only valid for the
   *    evaluation that populated it: /api/transactions/{id}/details resolves
   *    `is_comparable` and `changes` from that evaluation's pivot row.
   */
  const resetForEvaluation = () => {
    resetMarkedTransactions(false)
    transactionDetails.value = {}
    markedTableConf.value = {}
    hiddenComparisonGroups.value = []
    overriddenFields.value = {}
    originalFieldValues.value = {}

    // Rebuilds markedTableConf from scratch via initComparisonTableConfig().
    return resetComparableState()
  }

  /**
   * Debounced function that triggers adjustment recalculation with all accumulated overrides.
   * Waits 300ms after last update before firing, ensuring all rapid changes are batched.
   */
  const debouncedRecalculateAdjustments = debounce(async () => {
    const comparableIds = markedTransactions.value.filter(id => {
      const row = selectionTableRows.value.find(r => r.id === id)
      return row?.is_comparable === true
    })

    if (
      comparableIds.length === 0 ||
      !evaluationStore.prediction?.similar_properties?.length
    ) {
      return
    }

    // Send the FULL accumulated override set every time. The ML recompute is
    // stateless — it re-fetches each comparable's stored dataset row and applies
    // only the overrides in THIS request — so a drained field silently reverts to
    // its stored value (RED-262: editing a 2nd pivot field lost the 1st). Keep
    // accumulating; the set is cleared in resetComparableState().
    const overridesToSend = { ...pendingPivotOverrides.value }

    console.log(
      `Recalculating adjustments for ${comparableIds.length} comparables with overrides:`,
      overridesToSend,
    )

    try {
      const adjustmentData =
        await evaluationStore.calculateComparablesAdjustments(
          comparableIds,
          Object.keys(overridesToSend).length > 0 ? overridesToSend : null,
        )

      if (adjustmentData?.available) {
        await evaluationStore.updateComparablesAdjustments(adjustmentData)
        console.log('Auto-recalculated adjustments successfully')
      }
    } catch (error) {
      console.error('Failed to recalculate adjustments:', error)
    }
  }, 300) // 300ms debounce - waits for rapid updates to settle

  return {
    fetchAdArchive,
    retryAdArchive,
    setAdArchiveSummary,
    buildings,
    updateTransactionBuilding,
    restoreMarkedTransactions,
    objectId,
    transactionMapRequestParams,
    selectedTransaction,
    getOrLoadTransactionBuildings,
    getMarkedTransactions,
    updateTransaction,
    updateTransactionMaatukk,
    loadTransactionDetails,
    loadMarkedTransactionsDetails,
    loadRelatedTransactions,
    toggleMarked,
    searchMetadata,
    markedTransactions,
    transactionDetails,
    getTransactions,
    loadClusterTransactions,
    loadTransactionsByIds,
    getSelectedTransactions,
    selectTransaction,
    selectedTransactionIds,
    handleMapFeatureSelection,
    handleListFeatureSelection,
    selectedTransactions,
    transactions,
    selectedBuilding,
    selectionTableRows,
    selectedUnitPriceBase,
    masterOverrides,
    clearMasterOverrides,
    selectionTableExport,
    isTransactionsMapDialogOpen,
    masterData,
    markedTableConf,
    selectedPriceBase,
    comparisonTableExport,
    isSubjectHouse,
    updateSelectionTableRows,
    selectionTableRowsParsed,
    selectionTableFields,
    selectionTableSwitchState,
    getFilterMetadata,
    filterMetadataLoading,
    shouldInitTransactionFilters,
    transactionsAbortController,
    abortAndResetTransactionsAbortController,
    loadParcelBoundaries,
    updateComparisonTransactionField,
    initComparisonTableConfig,
    reconfigureForPropertyContext,
    comparableTransactions,
    processedComparableTransactions,
    processedMasterData,
    comparisonTableData,
    comparisonTableFields,
    comparisonTableRows,
    addComparisonCriteria,
    deleteComparisonCriteria,
    processedSelectionTableRows,
    selectionTableFieldsComputed,
    selectionTableRowsParsedComputed,
    updateSelectionTransactionField,
    toggleTransactionIsComparable,
    comparisonComponentPage,
    setComparisonPage,
    ensureTransactionIsInSelectionTable,
    updateUserDefinedFieldLabel,
    resetMarkedTransactions,
    updateTransactionBuildingPart,
    updateTransactionBuildingField,
    resetComparableState,
    clearComparableTable,
    resetForEvaluation,
    restoreCustomCriteria,
    hiddenComparisonGroups,
    hiddenComparisonKeys,
    markFieldOverridden,
    isFieldOverridden,
    getOriginalFieldValue,
    revertTransactionField,
    comparisonGroupMembers,
    isHideableComparisonGroup,
    hideComparisonGroup,
    unhideComparisonGroup,
    unhideAllComparisonGroups,
    restoreHiddenGroups,
    persistPriceMode,
    restorePriceMode,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTransactionStore, import.meta.hot))
}
